import type { NPSConfig } from '@/pages/settings/ats/survey/components/SurveySubmissionResponse.interface';
import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { KTag, KText } from '@/ui-library/components';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';

export const statusConfig = {
  published: {
    color: 'success-200',
    label: 'Open',
  },
  draft: {
    color: 'warning-200',
    label: 'Draft',
  },
  inactive: {
    color: 'error-200',
    label: 'Inactive',
  },
};

export const surveyTypeConfig = {
  candidate: {
    color: 'pastel-5-dark',
    bg: 'pastel-5-light',
    label: 'Candidate survey',
  },
  employee: {
    color: 'pastel-4-dark',
    bg: 'pastel-4-light',
    label: 'HM survey',
  },
};

export const surveyTabs = {
  DETAILS: 'details',
  QUESTIONS: 'questions',
  SUBMISSIONS: 'submissions',
  SUMMARY: 'summary',
};

export const surveyStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  INACTIVE: 'inactive',
  EDIT: 'edit',
};

/**
 * Survey Question Types Configuration
 * Maps all available question types used in the survey system
 */
export const questionType = {
  // Text inputs
  STRING: 'string',
  TEXT: 'text',

  // Numeric
  NUMBER: 'number',

  // Date/Time
  DATE: 'date',

  // URL
  URL: 'url',

  // Boolean
  ENUM_BOOLEAN: 'enum_boolean',

  // Selection types
  REL_TAG: 'rel_tag', // Single select dropdown
  REL_TAG_MULTI: 'rel_tag_multi', // Multi select dropdown

  // File upload
  REL_FILE: 'rel_file',

  // Rating types
  STAR_RATING: 'star_rating',
  NPS_RATING: 'nps_rating',
  SCALE_RATING: 'scale_rating',
};

/**
 * Question Type Labels
 * Human-readable labels for each question type
 */
export const questionTypeLabels = {
  [questionType.STRING]: 'Short Text',
  [questionType.TEXT]: 'Long Text',
  [questionType.NUMBER]: 'Number',
  [questionType.DATE]: 'Date',
  [questionType.URL]: 'URL',
  [questionType.ENUM_BOOLEAN]: 'Yes/No',
  [questionType.REL_TAG]: 'Single Select',
  [questionType.REL_TAG_MULTI]: 'Multi Select',
  [questionType.REL_FILE]: 'File Upload',
  [questionType.STAR_RATING]: 'Star Rating',
  [questionType.NPS_RATING]: 'NPS Rating',
  [questionType.SCALE_RATING]: 'Scale Rating',
};

/**
 * Question Type Categories
 * Groups question types by category for UI organization
 */
export const questionTypeCategories = {
  TEXT_INPUT: [questionType.STRING, questionType.TEXT],
  NUMERIC: [questionType.NUMBER],
  DATE_TIME: [questionType.DATE],
  SELECTION: [
    questionType.REL_TAG,
    questionType.REL_TAG_MULTI,
    questionType.ENUM_BOOLEAN,
  ],
  RATING: [
    questionType.STAR_RATING,
    questionType.NPS_RATING,
    questionType.SCALE_RATING,
  ],
  FILE: [questionType.REL_FILE],
  OTHER: [questionType.URL],
};

/**
 * Get NPS Configuration based on score value
 * @param value - NPS score value (0-10)
 * @returns NPSConfig with label, colors based on score category
 */
export const getNPSConfig = (
  value: number,
  enableTooltip: boolean = false
): NPSConfig => {
  if (value >= 9) {
    return {
      label: 'Promoter',
      bgColor: 'pastel-2-light',
      color: 'pastel-2-dark',
      scoreColor: 'pastel-2-dark',
      enableTooltip,
    };
  } else if (value >= 7 && value <= 8) {
    return {
      label: 'Passive',
      bgColor: 'pastel-6-light',
      color: 'pastel-6-dark',
      scoreColor: 'pastel-6-dark',
      enableTooltip,
    };
  } else if (value >= 0 && value <= 6) {
    return {
      label: 'Detractor',
      bgColor: 'pastel-1-light',
      color: 'pastel-1-dark',
      scoreColor: 'pastel-1-dark',
      enableTooltip,
    };
  } else {
    return {
      label: 'Invalid',
      bgColor: 'pastel-4-light',
      color: 'pastel-4-dark',
      scoreColor: 'pastel-4-dark',
      enableTooltip,
    };
  }
};

export const renderNPSRating = (
  value: number,
  maxValue: number,
  npsConfig: NPSConfig
) => (
  <Flex gap="6px" alignItems="center" flexWrap="wrap">
    <KTag
      bg={npsConfig.bgColor}
      w="fit-content"
      value={
        <Flex alignItems="center">
          <KText size="sm-medium" color={npsConfig.scoreColor}>
            {value}/
          </KText>
          <KText size="sm-medium" color={npsConfig.color}>
            {maxValue}
          </KText>
          {npsConfig.enableTooltip && (
            <Tooltip
              label={`Overall satisfaction score: ${value}`}
              placement="top"
            >
              <Box mt="-3px">
                <Icon
                  as={InfoCircle}
                  width="16px"
                  height="16px"
                  color={npsConfig.scoreColor}
                  ml="6px"
                />
              </Box>
            </Tooltip>
          )}
        </Flex>
      }
    />
  </Flex>
);
